import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pipe que ordena un arreglo por un campo dado y, opcionalmente, agrupa los
 * registros por fecha para insertar filas de subtotales de tonelaje y piezas.
 */
@Pipe({
  name: 'sort'
})

export class SortPipe implements PipeTransform {
  fieldsToGroupByDate: string[] = ['admission_date', 'departure_date', 'admission_date', 'entry_date'];
  /**
   * Ordena el arreglo recibido y, cuando aplica, agrega filas de subtotales agrupadas por fecha.
   * @param array - Arreglo de registros a ordenar.
   * @param field - Campo por el cual ordenar; si está vacío solo se agrupa por `groupByField`.
   * @param isAscending - `true` para orden ascendente, `false` para descendente.
   * @param groupByField - Campo de fecha por el que se agrupan los subtotales.
   * @param totalField - Campo cuyo valor se suma para el subtotal de tonelaje.
   * @param subtotalField - Campo donde se escribe la etiqueta del subtotal.
   * @param totalPiecesField - Campo cuyo valor se suma para el subtotal de piezas.
   * @returns El arreglo ordenado, con filas de subtotales si se solicitó la agrupación.
   */
  transform(array: any[], field: string, isAscending: boolean, groupByField: string = '', totalField: string = '', subtotalField: string = '', totalPiecesField: string = ''): any {
    if (!Array.isArray(array)) {
      return;
    }
    if (!field) {
      if (!groupByField)
        return array;
      const sortedArray = this.getSortedArray(array, groupByField, isAscending)
      return this.groupArrayToGetSubtotalsByDates(sortedArray, groupByField, totalField, subtotalField, totalPiecesField);
    }

    // Ordenar el array
    const sortedArray = this.getSortedArray(array, field, isAscending)

    // Abstraer logica a un metodo para ejecutar aqui y cuando (!field && addSubtotals) arriba en el pipe
    if (this.fieldsToGroupByDate.includes(field) && groupByField) {
      return this.groupArrayToGetSubtotalsByDates(sortedArray, groupByField, totalField, subtotalField, totalPiecesField);
    }

    return sortedArray;
  }

  /**
   * Agrupa los registros por fecha y construye un nuevo arreglo que intercala
   * filas de subtotales (tonelaje y piezas) al final de cada grupo de fecha.
   * @param array - Arreglo ya ordenado por la fecha de agrupación.
   * @param groupByField - Campo de fecha por el que se agrupan los registros.
   * @param totalField - Campo cuyo valor se suma para el subtotal de tonelaje.
   * @param subtotalField - Campo donde se escribe la etiqueta del subtotal.
   * @param totalPiecesField - Campo cuyo valor se suma para el subtotal de piezas.
   * @returns El arreglo con las filas de subtotales insertadas.
   */
  groupArrayToGetSubtotalsByDates(array: any[], groupByField: string, totalField: string, subtotalField: string, totalPiecesField: string) {
    const groupedData: { [date: string]: { totalWeight: number; totalPieces: number; count: number } } = {};

    // Sumar pesos por fecha y contar el número de registros
    array.forEach((item) => {
      const date = item[groupByField];
      const weight = Number(item[totalField]) || 0; // Asegurarse de que total_weight sea un número
      const pieces = Number(item[totalPiecesField]) || 0; // Asegurarse de que total_pieces sea un número

      if (!groupedData[date]) {
        groupedData[date] = { totalWeight: 0, totalPieces: 0, count: 0 };
      }
      groupedData[date].totalWeight += weight;
      groupedData[date].totalPieces += pieces;
      groupedData[date].count += 1;
    });

    // Construcción del array con resúmenes correctamente insertados
    const resultArray: any[] = [];

    array.forEach((item, index) => {
      resultArray.push({ ...item }); // Clonamos el objeto para evitar mutaciones

      // Verificar si es el último registro de la fecha
      const isLastOfDate =
        index === array.length - 1 ||
        array[index + 1][groupByField] !== item[groupByField];

      if (isLastOfDate) {
        const subtotalRow = {
          [subtotalField]: `Subtotal(${groupedData[item[groupByField]]?.count || 0}):`,
          [totalField]: groupedData[item[groupByField]]?.totalWeight || 0,
          subtototalRow: true
        };
        if (totalPiecesField)
          subtotalRow[totalPiecesField] = groupedData[item[groupByField]]?.totalPieces || 0;
        resultArray.push(subtotalRow);
      }
    });
    return resultArray;
  }

  /**
   * Ordena el arreglo por el campo indicado, dejando los valores nulos al final.
   * @param array - Arreglo de registros a ordenar.
   * @param field - Campo por el cual se ordena.
   * @param isAscending - `true` para orden ascendente, `false` para descendente.
   * @returns El mismo arreglo ordenado in situ.
   */
  getSortedArray(array: any[], field: string, isAscending: boolean): any[] {
    return array.sort((a, b) => {
      if (field === 'material_name') {
        if (b[field] === null) {
          b[field] = '';
        }
        if (a[field] === null) {
          a[field] = '';
        }
      }
      if (a[field] === null || a[field] === undefined) {
        return 1;
      }
      if (b[field] === null || b[field] === undefined) {
        return -1;
      }
      if (isAscending) {
        return a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0;
      } else {
        return b[field] < a[field] ? -1 : b[field] > a[field] ? 1 : 0;
      }
    });
  }
}
